<template>
  <div class="px-3">
    <v-col
      cols="12"

      class="body-bold-smaller py-0"
    >
      <a type="primary" size="xsmall" @click="beforeLeave">
        <v-icon size="12" class="backIcon">mdi-arrow-left</v-icon>
        {{ $t('message.actions.return') }}
      </a>
    </v-col>

    <v-col class="pt-1 py-0">
      <span class="body-bold-medium">{{
        $t('message.pages.videoSpeed.videoSpeed')
      }}</span>
    </v-col>

    <v-col cols="12" class="py-0">
      <span class="body-regular-smaller textType6--text pageDescription">
        {{ $t('message.pages.videoSpeed.videoSpeedMessage') }}
        <a target="_blank" type="secondary" :href="turboArticle">
          <span>
            {{ $t('message.embedModal.knowMore') }}
          </span>
          <v-icon size="12">mdi-open-in-new</v-icon>
        </a>
      </span>
    </v-col>

    <v-col class="pt-6" ref="topElement">
      <span class="body-bold-medium">Turbo</span>
    </v-col>

    <v-card flat>
      <v-row>
        <v-col cols="6" class="px-6">
          <p class="body-regular-small mb-1">
            {{ $t('message.pages.videoSpeed.activeTurbo') }}
          </p>
          <p class="body-regular-smaller textType6--text">
            {{ $t('message.pages.videoSpeed.modifyInitialSpeed') }}
          </p>
        </v-col>
        <v-col cols="6" class="py-0 d-flex align-center">
          <v-select
            v-model="videoSpeed"
            :items="videoSpeedOptions"
            item-text="label"
            item-value="value"
            :disabled="loadingSlider"
          ></v-select>
        </v-col>
      </v-row>
    </v-card>

    <portal to="actions-footer">
      <div class="d-flex flex-row actions-row" v-if="hasEditVideoPerm">
        <v-spacer></v-spacer>
        <v-tooltip max-width="184px" top :disabled="!disableBtn">
          <template v-slot:activator="{ on, attrs }">
            <div class="pl-2" v-bind="attrs" v-on="on">
              <base-btn
                type="tertiary"
                class="mr-2 px-4"
                text
                :disabled="disableBtn"
                @click="discardChanges"
              >
                {{ $t('message.cancel') }}
              </base-btn>
            </div>
          </template>
          <span>{{ $t('message.noChanges') }}</span>
        </v-tooltip>
        <v-tooltip max-width="184px" top :disabled="!disableBtn">
          <template v-slot:activator="{ on, attrs }">
            <div class="pl-2" v-bind="attrs" v-on="on">
              <base-btn
                :class="{ 'd-none': loadingSlider }"
                type="primary"
                :disabled="loadingButtonSave || disableBtn"
                :loading="loadingButtonSave"
                @click="handleUpdateVideoSpeed"
              >
                {{ $t('message.pages.video.publish') }}
              </base-btn>
            </div>
          </template>
          <span>{{ $t('message.noChanges') }}</span>
        </v-tooltip>
      </div>
    </portal>
    <unsaved-changes-modal />
  </div>
</template>
<script>
import { mapActions, mapState, mapMutations } from 'vuex'
import profileApi from '@/services/profileApi'
import linkHelper from '@/utils/linkHelper.mixin.js'
import i18n from '@/translations/lang'

import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue'

export default {
  name: 'VideoPlayerSpeed',
  components: {
    UnsavedChangesModal,
  },
  mixins: [linkHelper],
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    hasEdit: {
      type: Array,
    },
  },
  data: () => ({
    tickLabels: ['Normal', '1.1x', '1.2x', '1.3x', '1.4x', '1.5x'],
    loadingButtonSave: false,
    cardHeight: 0,
    videoSpeedOptions: [
      { value: 1, label: '1 (Normal)' },
      { value: 1.1, label: '1.1x' },
      { value: 1.2, label: '1.2x' },
      { value: 1.3, label: '1.3x' },
      { value: 1.4, label: '1.4x' },
      { value: 1.5, label: '1.5x' },
    ],
    withoutChanges: true,
    videoId: null,
    videoSpeed: null,
    videoSpeedList: [1, 1.1, 1.2, 1.3, 1.4, 1.5],
    defaultSpeed: 0,
    disableBtn: true,
    loadingSlider: true,
  }),
  watch: {
    videoSpeed: {
      handler() {
        this.setButtonDisabling()
      },
    },
  },
  mounted() {
    this.videoId = this.liveVideoId ? this.liveVideoId : this.$route.params.id
    this.getDefaultSpeed()
    this.setButtonDisabling()
    this.hasEdit.push(this.checkChanges)
    this.$root.$on('discardChanges', () => {
      this.discardChanges()
      this.handleBack()
    })
  },
  beforeDestroy() {
    this.$root.$off('discardChanges')
  },
  computed: {
    ...mapState({
      profile: (state) => state.video.profile,
      video: (state) => state.video.video,
      profile_id: (state) => state.video.profile_id,
    }),
    locale() {
      return this.$i18next.resolvedLanguage
    },
    turboArticle() {
      if (this.locale === 'en') {
        return 'https://help.pandavideo.com/en-us/article/how-does-the-playback-speed-turbo-feature-works-and-how-do-you-activate-it-at-panda-1c0o13p/'
      }
      if (this.locale === 'es') {
        return 'https://help.pandavideo.com/es/article/como-funciona-y-como-activar-la-velocidad-de-reproduccion-turbo-en-panda-1sp1z8o/'
      }
      return 'https://help.pandavideo.com/pt-br/article/como-funciona-e-como-ativar-a-velocidade-de-reproducao-turbo-na-panda-v5z4id/'
    },
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    ...mapActions('video', [
      'updateProfile',
      'createProfile',
      'setProfileToVideos',
      'getVideoConfig',
    ]),
    ...mapMutations('video', ['UPDATE_VIDEO_PROFILE', 'UPDATE_STATUS_ERROR']),
    beforeLeave() {
      if (!this.checkChanges()) {
        this.$root.$emit('unsavedChangesModal')
      } else {
        this.UPDATE_STATUS_ERROR(false)
        this.handleBack()
      }
    },
    async handleUpdateVideoSpeed() {
      this.loadingButtonSave = true
      try {
        await this.updateProfile([
          this.profileId,
          {
            general: {
              default_speed: this.videoSpeed,
            },
          },
        ])

        this.UPDATE_STATUS_ERROR(false)
        this.UPDATE_VIDEO_PROFILE(this.profile)
        this.loadingButtonSave = false
        this.defaultSpeed = this.videoSpeed
        this.handleSnackbar(this.$t('message.pages.video.changesSaved'))
        this.setButtonDisabling()
      } catch (e) {
        console.error(e)
        this.loadingButtonSave = false
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },

    async setProfile() {
      if (!this.video.config.profile) {
        await this.createProfile(this.videoId)
        this.video.config.profile = this.profile_id
        this.profileId = this.profile_id
        await this.setProfileToVideos([this.profileId, this.videoId])
      } else {
        this.profileId = this.video.config.profile
      }
    },
    discardChanges() {
      this.videoSpeed = this.defaultSpeed
      this.setButtonDisabling()
    },
    async getDefaultSpeed() {
      await this.setProfile()
      const profileId = this.video.config.profile
      const {
        data: {
          general: { default_speed },
        },
      } = await profileApi.getProfileId(profileId)

      this.videoSpeed =
        this.videoSpeedOptions.find((option) => option.value === default_speed)
          ?.value || this.videoSpeedOptions[0].value
      this.defaultSpeed = this.videoSpeed
      this.handleSkeletonLoader()
    },
    handleSkeletonLoader() {
      this.loadingSlider = false
    },
    handleSnackbar(item) {
      if (!item) {
        item = i18n.t('message.changesSaved')
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: item,
        type: 'success',
      })
    },
    setButtonDisabling() {
      this.disableBtn = this.defaultSpeed === this.videoSpeed ? true : false
    },
    handleKnowMore() {
      const link = this.getVideoSpeedLink()
      window.open(link, '_blank')
    },
    checkChanges() {
      return this.defaultSpeed === this.videoSpeed ? true : false
    },
  },
}
</script>
<style scoped lang="scss">
::v-deep .v-input__slider {
  .v-input__control {
    .v-input__slot {
      .v-slider__ticks-container {
        .v-slider__tick {
          opacity: 1 !important;
          height: 10px !important;
          width: 2px !important;
          top: -4px !important;
          .v-slider__tick-label {
            top: 16px !important;
          }
          &:first-child {
            .v-slider__tick-label {
              color: var(--v-primary-base) !important;
              font-weight: bold !important;
            }
          }
        }
      }
      .v-slider__track-container {
        height: 10px;
        .v-slider__track-background {
          background-color: #e3e3e8 !important;
        }
      }
      .v-slider__thumb-container {
        .v-slider__thumb {
          height: 20px !important;
          width: 20px !important;
          &::before {
            top: -8px !important;
            left: -8px !important;
          }
        }
      }
    }
  }
}
.loader-width {
  width: 100%;
}
.backIcon {
  color: varFigma(brand-60-base) !important;
}

.backBtn {
  cursor: pointer;
}

.skeleton-btn-loader-container {
  display: flex;
  justify-content: flex-end;
}
::v-deep .skeleton-button {
  .v-skeleton-loader__button {
    width: 130px;
    height: 36px;
  }
}
::v-deep .skeleton-slider {
  .v-skeleton-loader__image {
    height: 100px;
  }
}
</style>
